/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  HookdeckCredentials,
  HookdeckCredentials$inboundSchema,
  HookdeckCredentials$Outbound,
  HookdeckCredentials$outboundSchema,
} from "./hookdeckcredentials.js";
import {
  Topics,
  Topics$inboundSchema,
  Topics$Outbound,
  Topics$outboundSchema,
} from "./topics.js";

export type DestinationHookdeck = {
  /**
   * Control plane generated ID or user provided ID for the destination.
   */
  id: string;
  /**
   * Type of the destination.
   */
  type: "hookdeck";
  /**
   * "*" or an array of enabled topics.
   */
  topics: Topics;
  /**
   * Optional JSON schema filter for event matching. Events must match this filter to be delivered to this destination.
   *
   * @remarks
   * Supports operators: $eq, $neq, $gt, $gte, $lt, $lte, $in, $nin, $startsWith, $endsWith, $exist, $or, $and, $not.
   * If null or empty, all events matching the topic filter will be delivered.
   * To remove an existing filter when updating a destination, set filter to an empty object `{}`.
   */
  filter?: { [k: string]: any } | null | undefined;
  /**
   * ISO Date when the destination was disabled, or null if enabled.
   */
  disabledAt: Date | null;
  /**
   * ISO Date when the destination was created.
   */
  createdAt: Date;
  /**
   * ISO Date when the destination was last updated.
   */
  updatedAt?: Date | undefined;
  config?: any | undefined;
  credentials: HookdeckCredentials;
  /**
   * Static key-value pairs merged into event metadata on every attempt.
   */
  deliveryMetadata?: { [k: string]: string } | null | undefined;
  /**
   * Arbitrary contextual information stored with the destination.
   */
  metadata?: { [k: string]: string } | null | undefined;
  /**
   * A human-readable representation of the destination target (Hookdeck). Read-only.
   */
  target?: string | undefined;
  /**
   * A URL link to the destination target (e.g., Hookdeck dashboard). Read-only.
   */
  targetUrl?: string | null | undefined;
};

/** @internal */
export const DestinationHookdeck$inboundSchema: z.ZodType<
  DestinationHookdeck,
  z.ZodTypeDef,
  unknown
> = z.object({
  id: z.string(),
  type: z.literal("hookdeck"),
  topics: Topics$inboundSchema,
  filter: z.nullable(z.record(z.any())).optional(),
  disabled_at: z.nullable(
    z.string().datetime({ offset: true }).transform(v => new Date(v)),
  ),
  created_at: z.string().datetime({ offset: true }).transform(v => new Date(v)),
  updated_at: z.string().datetime({ offset: true }).transform(v => new Date(v))
    .optional(),
  config: z.any().optional(),
  credentials: HookdeckCredentials$inboundSchema,
  delivery_metadata: z.nullable(z.record(z.string())).optional(),
  metadata: z.nullable(z.record(z.string())).optional(),
  target: z.string().optional(),
  target_url: z.nullable(z.string()).optional(),
}).transform((v) => {
  return remap$(v, {
    "disabled_at": "disabledAt",
    "created_at": "createdAt",
    "updated_at": "updatedAt",
    "delivery_metadata": "deliveryMetadata",
    "target_url": "targetUrl",
  });
});
/** @internal */
export type DestinationHookdeck$Outbound = {
  id: string;
  type: "hookdeck";
  topics: Topics$Outbound;
  filter?: { [k: string]: any } | null | undefined;
  disabled_at: string | null;
  created_at: string;
  updated_at?: string | undefined;
  config?: any | undefined;
  credentials: HookdeckCredentials$Outbound;
  delivery_metadata?: { [k: string]: string } | null | undefined;
  metadata?: { [k: string]: string } | null | undefined;
  target?: string | undefined;
  target_url?: string | null | undefined;
};

/** @internal */
export const DestinationHookdeck$outboundSchema: z.ZodType<
  DestinationHookdeck$Outbound,
  z.ZodTypeDef,
  DestinationHookdeck
> = z.object({
  id: z.string(),
  type: z.literal("hookdeck"),
  topics: Topics$outboundSchema,
  filter: z.nullable(z.record(z.any())).optional(),
  disabledAt: z.nullable(z.date().transform(v => v.toISOString())),
  createdAt: z.date().transform(v => v.toISOString()),
  updatedAt: z.date().transform(v => v.toISOString()).optional(),
  config: z.any().optional(),
  credentials: HookdeckCredentials$outboundSchema,
  deliveryMetadata: z.nullable(z.record(z.string())).optional(),
  metadata: z.nullable(z.record(z.string())).optional(),
  target: z.string().optional(),
  targetUrl: z.nullable(z.string()).optional(),
}).transform((v) => {
  return remap$(v, {
    disabledAt: "disabled_at",
    createdAt: "created_at",
    updatedAt: "updated_at",
    deliveryMetadata: "delivery_metadata",
    targetUrl: "target_url",
  });
});

export function destinationHookdeckToJSON(
  destinationHookdeck: DestinationHookdeck,
): string {
  return JSON.stringify(
    DestinationHookdeck$outboundSchema.parse(destinationHookdeck),
  );
}
export function destinationHookdeckFromJSON(
  jsonString: string,
): SafeParseResult<DestinationHookdeck, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => DestinationHookdeck$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'DestinationHookdeck' from JSON`,
  );
}
